import { useCallback, useMemo, useState } from 'react'
import { useSlate } from 'slate-react'

import { Button } from 'core-components/library/button'
import { DragNDropFileUploader, IFileSettings } from 'core-components/library/dragNDropFileUploader'
import { Flex } from 'core-components/library/flex'
import {
  IMAGE_AVAILABLE_MIME_TYPES,
  VIDEO_AVAILABLE_MIME_TYPES,
} from 'core-components/library/richTextEditor/constants'
import { MAX_FILE_SIZE } from 'core-components/library/richTextEditor/helpers/imageHelper'
import { isValidUrl, ValidateInput } from 'core-components/library/validation'

import { EMediaType } from './interfaces'

import styles from './MediaDragNDrop.module.scss'

export const MediaDragNDrop = ({
  onUrlChange,
  fileUrl,
  type,
  onFileChange,
}: {
  onUrlChange: (value: string) => void
  fileUrl: string
  type: EMediaType
  onFileChange: (value: IFileSettings) => void
}) => {
  const [file, setFile] = useState<IFileSettings | null | undefined>(undefined)
  const { fileUploadAdapter } = useSlate()

  const changeFileLoader = useCallback(
    async (files: IFileSettings[]) => {
      setFile(files[0])
      onFileChange(files[0])
    },
    [onFileChange]
  )

  const getMimeTypesByType = useCallback(() => {
    if (type === EMediaType.Image) {
      return IMAGE_AVAILABLE_MIME_TYPES
    }
    return VIDEO_AVAILABLE_MIME_TYPES
  }, [type])

  const acceptedFormats = useMemo(() => {
    const mimeTypes = getMimeTypesByType()
    return mimeTypes.map((item) => item.value).join(', ')
  }, [getMimeTypesByType])

  const maxFileSize = useMemo(() => {
    if (type === EMediaType.Image) {
      return MAX_FILE_SIZE
    }
    return undefined
  }, [type])

  const removeFile = useCallback(() => {
    setFile(null)
  }, [])

  return (
    <div className={'margin-top-xl'}>
      <Flex
        alignItems={'baseline'}
        justifyContent={'space-between'}
        width={'100%'}
        className={'margin-bottom-sm'}
      >
        {!file ? (
          <>
            <div>Link:</div>
            <ValidateInput
              value={fileUrl}
              onChange={(value) => onUrlChange(value)}
              placeholder={`Add ${type} link`}
              checkFn={isValidUrl}
              classes={{ validatorClasses: { root: styles.settingsLine__value } }}
            />
          </>
        ) : null}
      </Flex>
      {fileUploadAdapter ? (
        <div>
          <Flex className={styles.divider} alignItems={'center'} gap={'none'}>
            <span className={styles.divider__text}>Or</span>
          </Flex>
          <Flex
            alignItems={'baseline'}
            justifyContent={'space-between'}
            width={'100%'}
            className={'margin-bottom-sm'}
          >
            <div className={styles.dndArea}>
              {file ? (
                <div className={styles.dndArea__file}>
                  <div className={styles.dndArea__fileTitle}>{file.file.name}</div>
                  <Button iconButton={'kbase kbase-close'} onClick={() => removeFile()} />
                </div>
              ) : null}
              <DragNDropFileUploader
                changeFileLoader={changeFileLoader}
                acceptedFormats={acceptedFormats}
                urlInputVisibility={false}
                maxFileSize={maxFileSize}
                fileListVisibility={false}
              />
            </div>
          </Flex>
        </div>
      ) : null}
    </div>
  )
}
